import React, { useContext } from "react";
import { Modal, Space, Tag } from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    RightOutlined,
    ShopOutlined,
    TeamOutlined,
} from "@ant-design/icons";
import { ISocialInstitution } from "../../../../interfaces";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import { formatCnpj } from "../../../../utils/Masks";
import { poeValorComPontosEmMil } from "../../../../utils/Conversores";

interface IProps {
    open: boolean;
    onClose: () => void;
    socialInstitution: ISocialInstitution | null;
    onViewUsers: (socialInstitution: ISocialInstitution) => void;
    onEdit: (socialInstitution: ISocialInstitution) => void;
    onDelete: (socialInstitution: ISocialInstitution) => void;
}

export default function ModalSocialInstitutionActions({
    open,
    onClose,
    socialInstitution,
    onViewUsers,
    onEdit,
    onDelete,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    if (!socialInstitution) {
        return null;
    }

    const supportersCount = socialInstitution.total_users ?? 0;

    const actionItems = [
        {
            id: "view_users",
            title: "Ver Apoiadores",
            description: `${poeValorComPontosEmMil(supportersCount)} munícipe(s) vinculado(s) apoiando esta entidade`,
            icon: <TeamOutlined style={{ fontSize: 20, color: "#eb2f96" }} />,
            iconBg: isDark ? "rgba(235, 47, 150, 0.15)" : "#fff0f6",
            iconBorder: isDark ? "rgba(235, 47, 150, 0.3)" : "#ffd6e7",
            hoverBorder: "#eb2f96",
            onClick: () => {
                onClose();
                onViewUsers(socialInstitution);
            },
        },
        {
            id: "edit",
            title: "Editar Instituição",
            description: "Alterar nome, CNPJ ou status de ativação",
            icon: <EditOutlined style={{ fontSize: 20, color: "#1677ff" }} />,
            iconBg: isDark ? "rgba(22, 119, 255, 0.15)" : "#e6f4ff",
            iconBorder: isDark ? "rgba(22, 119, 255, 0.3)" : "#bae0ff",
            hoverBorder: "#1677ff",
            onClick: () => {
                onClose();
                onEdit(socialInstitution);
            },
        },
        {
            id: "delete",
            title: "Excluir Instituição",
            description: "Remover permanentemente esta entidade do sistema",
            icon: <DeleteOutlined style={{ fontSize: 20, color: "#ff4d4f" }} />,
            iconBg: isDark ? "rgba(255, 77, 79, 0.15)" : "#fff1f0",
            iconBorder: isDark ? "rgba(255, 77, 79, 0.3)" : "#ffccc7",
            hoverBorder: "#ff4d4f",
            onClick: () => {
                onClose();
                onDelete(socialInstitution);
            },
        },
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            centered
            width={520}
            title={
                <Space align="start" size={12}>
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            background: isDark ? "rgba(22, 119, 255, 0.15)" : "#e6f4ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <ShopOutlined style={{ color: "#1677ff", fontSize: 20 }} />
                    </div>
                    <div>
                        <div
                            style={{
                                fontWeight: 700,
                                fontSize: 16,
                                lineHeight: 1.3,
                                color: isDark ? "#f0f0f0" : "#1e293b",
                            }}
                        >
                            {socialInstitution.name}
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                flexWrap: "wrap",
                                marginTop: 6,
                            }}
                        >
                            {socialInstitution.cnpj && (
                                <Tag
                                    style={{
                                        margin: 0,
                                        fontFamily: "monospace",
                                        borderRadius: 4,
                                        fontSize: 12,
                                    }}
                                >
                                    {formatCnpj(socialInstitution.cnpj)}
                                </Tag>
                            )}
                            <Tag
                                color={socialInstitution.active ? "success" : "error"}
                                style={{ margin: 0, borderRadius: 4, fontSize: 12 }}
                            >
                                {socialInstitution.active ? "Ativo" : "Inativo"}
                            </Tag>
                        </div>
                    </div>
                </Space>
            }
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    marginTop: 18,
                    marginBottom: 6,
                }}
            >
                {actionItems.map((item) => (
                    <div
                        key={item.id}
                        onClick={item.onClick}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "14px 16px",
                            borderRadius: 10,
                            background: isDark ? "#1f1f1f" : "#ffffff",
                            border: `1px solid ${isDark ? "#303030" : "#e2e8f0"}`,
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = item.hoverBorder;
                            e.currentTarget.style.background = isDark ? "#262626" : "#f8fafc";
                            e.currentTarget.style.transform = "translateY(-1px)";
                            e.currentTarget.style.boxShadow = isDark
                                ? "0 4px 12px rgba(0,0,0,0.3)"
                                : "0 4px 12px rgba(0,0,0,0.05)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = isDark ? "#303030" : "#e2e8f0";
                            e.currentTarget.style.background = isDark ? "#1f1f1f" : "#ffffff";
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                            <div
                                style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 10,
                                    background: item.iconBg,
                                    border: `1px solid ${item.iconBorder}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                {item.icon}
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontWeight: 600,
                                        fontSize: 14,
                                        color: isDark ? "#f0f0f0" : "#1e293b",
                                    }}
                                >
                                    {item.title}
                                </div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: isDark ? "#8c8c8c" : "#64748b",
                                        marginTop: 2,
                                    }}
                                >
                                    {item.description}
                                </div>
                            </div>
                        </div>
                        <RightOutlined
                            style={{
                                color: isDark ? "#595959" : "#94a3b8",
                                fontSize: 13,
                                marginLeft: 8,
                            }}
                        />
                    </div>
                ))}
            </div>
        </Modal>
    );
}
